import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Shield, UserPlus } from "lucide-react";

const ROLE_TONE = {
  admin: "bg-purple-50 text-purple-800 border-purple-200",
  investigator: "bg-blue-50 text-blue-800 border-blue-200",
  analyst: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "investigator" });
  const [showForm, setShowForm] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ email: "", name: "", password: "", role: "investigator" });
      setShowForm(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create user"); }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/admin/users/${id}`, { role }); toast.success("Role updated"); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not update"); }
  };
  const toggleActive = async (id, active) => {
    try { await api.patch(`/admin/users/${id}`, { active }); toast.success(active ? "Enabled" : "Disabled"); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="admin-users-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-purple-700 flex items-center gap-1.5"><Shield className="h-4 w-4" /> Administration</div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">Users &amp; roles</h1>
          <p className="text-sm text-slate-500 mt-1">Admin manages accounts. Analysts are read-only; investigators can edit; admins manage everything.</p>
        </div>
        <Button data-testid="new-user-btn" onClick={() => setShowForm((s) => !s)} className="bg-slate-900 hover:bg-slate-800 gap-1.5"><UserPlus className="h-4 w-4" /> Add user</Button>
      </header>

      {showForm && (
        <form onSubmit={createUser} data-testid="user-form" className="border border-slate-200 bg-white rounded-md p-5 grid sm:grid-cols-2 gap-3">
          <Input data-testid="user-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <Input data-testid="user-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Input data-testid="user-password" type="password" placeholder="Password (min 8 chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <div className="flex gap-2">
            <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-slate-300 rounded-md text-sm px-2 py-2 flex-1">
              <option value="investigator">Investigator</option>
              <option value="analyst">Analyst (read-only)</option>
              <option value="admin">Admin</option>
            </select>
            <Button data-testid="user-create-submit" type="submit" className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </div>
        </form>
      )}

      <section className="border border-slate-200 bg-white rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500" data-testid="admin-users-loading">Loading users…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Name</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Role</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-2.5 font-medium">{u.name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{u.email}</td>
                  <td className="px-4 py-2.5">
                    <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => changeRole(u.id, e.target.value)}
                      className={`border rounded-md text-xs px-2 py-1 font-mono ${ROLE_TONE[u.role] || ROLE_TONE.analyst}`}>
                      <option value="investigator">investigator</option>
                      <option value="analyst">analyst</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-2.5">
                    <button data-testid={`toggle-active-${u.id}`} onClick={() => toggleActive(u.id, u.active === false)}
                      className={`text-xs font-mono px-2 py-1 rounded-md border ${u.active === false ? "bg-red-50 text-red-700 border-red-200" : "bg-teal-50 text-teal-700 border-teal-200"}`}>
                      {u.active === false ? "disabled" : "active"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
